/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { resolveRubricVersion } from '../../../../common/util/evaluation/evalRubricVersionUtils';

const coerceOptionalNumber = (value) => {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return null;
};

const coerceOptionalString = (value) => {
  if (value == null) {
    return value;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object') {
    if (value.$oid != null) {
      return String(value.$oid);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const unwrapExecutionReportPayload = (response) => {
  if (response == null) {
    return null;
  }

  let data = response?.data ?? response;
  if (!data || typeof data !== 'object') {
    return null;
  }

  if (data.evaluationScores && typeof data.evaluationScores === 'object') {
    data = data.evaluationScores;
  }

  if (data.data && typeof data.data === 'object' && !data.rubricText && !data.scores && !data.executionId) {
    data = data.data;
  }

  return data;
};

const normalizeFeedback = (feedback) => {
  if (!feedback || typeof feedback !== 'object') {
    return null;
  }
  return {
    criteria: coerceOptionalString(feedback.criteria),
    score: coerceOptionalNumber(feedback.score),
    rationale: coerceOptionalString(feedback.rationale)
  };
};

const normalizeEvaluationItem = (item) => {
  if (!item || typeof item !== 'object') {
    return null;
  }
  return {
    auditId: coerceOptionalString(item.auditId),
    turnNumber: coerceOptionalNumber(item.turnNumber),
    input: coerceOptionalString(item.input),
    output: coerceOptionalString(item.output),
    requestId: coerceOptionalString(item.requestId),
    traceId: coerceOptionalString(item.traceId),
    auditDate: coerceOptionalString(item.auditDate),
    graphVersion: coerceOptionalNumber(item.graphVersion)
  };
};

const normalizeScoreDocument = (scoreDoc) => {
  if (!scoreDoc || typeof scoreDoc !== 'object') {
    return null;
  }

  const criterionScores = Array.isArray(scoreDoc.scores) ? scoreDoc.scores.map(normalizeFeedback).filter(Boolean) : [];
  const evaluationItems = Array.isArray(scoreDoc.evaluationItems)
    ? scoreDoc.evaluationItems.map(normalizeEvaluationItem).filter(Boolean)
    : [];

  return {
    index: coerceOptionalNumber(scoreDoc.index),
    userId: coerceOptionalString(scoreDoc.userId),
    evaluationItems,
    scores: criterionScores
  };
};

const normalizeScoreReferenceInterval = (interval) => {
  if (!interval || typeof interval !== 'object') {
    return null;
  }

  return {
    criterionOrField: coerceOptionalString(interval.criterionOrField ?? interval.criterion ?? interval.field),
    minimum: coerceOptionalString(interval.minimum ?? interval.min),
    maximum: coerceOptionalString(interval.maximum ?? interval.max)
  };
};

const normalizeExecutionReportSnapshot = (response) => {
  const data = unwrapExecutionReportPayload(response);
  if (!data || typeof data !== 'object') {
    return null;
  }

  const rawIntervals = data.scoreReferenceInterval ?? data.score_reference_interval;
  const scores = Array.isArray(data.scores) ? data.scores.map(normalizeScoreDocument).filter(Boolean) : [];
  const scoreReferenceInterval = Array.isArray(rawIntervals)
    ? rawIntervals.map(normalizeScoreReferenceInterval).filter(Boolean)
    : [];

  return {
    executionId: coerceOptionalString(data.executionId),
    _id: coerceOptionalString(data._id),
    rubricText: coerceOptionalString(data.rubricText),
    graphVersion: coerceOptionalNumber(data.graphVersion),
    rubricVersion: resolveRubricVersion(data.rubricVersion),
    createdDate: coerceOptionalString(data.createdDate),
    status: coerceOptionalString(data.status),
    executedBy: coerceOptionalString(data.executedBy),
    evalName: coerceOptionalString(data.evalName),
    rubricName: coerceOptionalString(data.rubricName),
    scoresCount: coerceOptionalNumber(data.scoresCount),
    scores,
    scoreReferenceInterval
  };
};

export const normalizeScoreTextContent = (response) => {
  const payload = unwrapExecutionReportPayload(response);
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  return {
    auditId: coerceOptionalString(payload.auditId),
    textContent: Array.isArray(payload.textContent) ? payload.textContent : []
  };
};

export const normalizePaginatedScoresPage = (data) => {
  const payload = unwrapExecutionReportPayload(data);
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const offset = Number(payload.offset) || 0;
  const limit = Number(payload.limit) || 0;
  const scores = Array.isArray(payload.scores) ? payload.scores.map(normalizeScoreDocument).filter(Boolean) : [];
  const base = { offset, limit, scores };
  if (offset !== 0) {
    return base;
  }
  const rawIntervals = payload.scoreReferenceInterval ?? payload.score_reference_interval;
  return {
    ...base,
    executionId: coerceOptionalString(payload.executionId),
    evalName: coerceOptionalString(payload.evalName),
    graphVersion: coerceOptionalNumber(payload.graphVersion),
    rubricVersion: resolveRubricVersion(payload.rubricVersion),
    executionType: coerceOptionalString(payload.executionType),
    isConversational: payload.isConversational === true,
    rubricText: coerceOptionalString(payload.rubricText),
    reportStats: payload.reportStats != null && typeof payload.reportStats === 'object' ? payload.reportStats : null,
    scoreReferenceInterval: Array.isArray(rawIntervals)
      ? rawIntervals.map(normalizeScoreReferenceInterval).filter(Boolean)
      : [],
    scoresCount: coerceOptionalNumber(payload.scoresCount) ?? 0
  };
};

export const normalizeBatchReportResponse = (response) => {
  if (response == null) {
    return null;
  }
  const payload = response?.data?.data ?? response?.data ?? response;
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  return {
    evalName: payload.evalName ?? null,
    rubricName: payload.rubricName ?? null,
    graphVersion: payload.graphVersion ?? null,
    rubricVersion: resolveRubricVersion(payload.rubricVersion),
    createdDate: payload.createdDate ?? null,
    completedDate: payload.completedDate ?? null,
    overallStatus: payload.overallStatus ?? null,
    samplingWindow: payload.samplingWindow ?? null,
    totalRecordBatches: payload.totalRecordBatches ?? 0,
    recordbatches: Array.isArray(payload.recordbatches) ? payload.recordbatches : []
  };
};

export default normalizeExecutionReportSnapshot;
